import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Loader2,
  ArrowLeft,
  CheckCircle2,
  XCircle,
  MessageSquare,
  KeyRound,
  AtSign,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import PasswordInput from "@/components/ui/PasswordInput";
import AuthShell from "@/components/auth/AuthShell";

/**
 * Forgot Password (SMS OTP) — three-step wizard rendered inside AuthShell.
 *
 * Feb 2026 rewrite (spec-driven):
 *   Step 1 · Identify   : ONLY registered email OR registered phone (E.164).
 *                          Username is intentionally NOT accepted.
 *   Step 2 · Verify     : 6-digit SMS code delivered to the phone tied to
 *                          the matched account. UI never confirms whether
 *                          the identifier exists — a generic message is
 *                          shown for every submission.
 *   Step 3 · New password: min 8 chars, ≥1 letter, ≥1 digit, no
 *                          leading/trailing whitespace, both fields must
 *                          match. Live checklist + submit disabled until
 *                          all rules pass.
 */

// ---------------------------------------------------------------------------
// Identifier validators — kept in-file because they're only used here.
// ---------------------------------------------------------------------------
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const E164_RE = /^\+[1-9]\d{6,14}$/;

function normalisePhone(raw) {
  if (!raw) return "";
  const stripped = raw.replace(/[\s\-().]/g, "");
  if (!stripped) return "";
  return stripped.startsWith("+") ? stripped : "+" + stripped.replace(/^0+/, "");
}

function classifyIdentifier(input) {
  const val = (input || "").trim();
  if (!val) return { kind: "empty", ok: false };
  if (val.includes("@")) {
    return { kind: "email", ok: EMAIL_RE.test(val), normalised: val };
  }
  const phone = normalisePhone(val);
  if (phone) {
    return { kind: "phone", ok: E164_RE.test(phone), normalised: phone };
  }
  return { kind: "unknown", ok: false };
}

// ---------------------------------------------------------------------------
// Password rules — mirror the backend policy exactly.
// ---------------------------------------------------------------------------
function usePasswordRules(t) {
  return useMemo(
    () => [
      {
        key: "length",
        label: t("pwreset.pw_rule_length", { defaultValue: "At least 8 characters" }),
        test: (p) => (p || "").length >= 8,
      },
      {
        key: "letter",
        label: t("pwreset.pw_rule_letter", { defaultValue: "At least one letter" }),
        test: (p) => /[A-Za-z]/.test(p || ""),
      },
      {
        key: "digit",
        label: t("pwreset.pw_rule_digit", { defaultValue: "At least one number" }),
        test: (p) => /[0-9]/.test(p || ""),
      },
      {
        key: "no_edge_space",
        label: t("pwreset.pw_rule_no_edge_space", {
          defaultValue: "No spaces at the start or end",
        }),
        // Only counts as satisfied once the user has typed at least one
        // character — an empty password shouldn't visually "pass" the
        // whitespace rule even though ''.trim() === ''.
        test: (p) => !!p && p === p.trim(),
      },
    ],
    [t],
  );
}

export default function ForgotPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [identifier, setIdentifier] = useState("");
  const [identifierTouched, setIdentifierTouched] = useState(false);
  const [requestId, setRequestId] = useState(null);
  const [code, setCode] = useState("");
  const [verificationToken, setVerificationToken] = useState(null);
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [genericSent, setGenericSent] = useState(false);
  const [codeErr, setCodeErr] = useState("");

  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef(null);
  useEffect(() => {
    if (cooldown <= 0) return;
    timerRef.current = setTimeout(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(timerRef.current);
  }, [cooldown]);

  const idInfo = useMemo(() => classifyIdentifier(identifier), [identifier]);
  const rules = usePasswordRules(t);
  const ruleState = useMemo(
    () => rules.map((r) => ({ ...r, ok: r.test(pw1) })),
    [rules, pw1],
  );
  const allRulesOk = ruleState.every((r) => r.ok);
  const passwordsMatch = pw1.length > 0 && pw1 === pw2;

  // -------------------------------------------------------------------------
  // Step 1 → POST /auth/password-reset/start
  // -------------------------------------------------------------------------
  const startReset = async (e) => {
    e?.preventDefault?.();
    setIdentifierTouched(true);
    if (!idInfo.ok) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/password-reset/start", {
        identifier: idInfo.normalised,
      });
      // We NEVER reveal whether the identifier resolved to a user.
      // The backend already returns the same shape either way, but to be
      // defence-in-depth we ignore anything the response might leak and
      // move straight to the generic "if valid, code has been sent" screen.
      setRequestId(data.request_id || null);
      setGenericSent(true);
      setCooldown(60);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error_generic"));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (!requestId || cooldown > 0) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/password-reset/resend", {
        request_id: requestId,
      });
      toast.success(
        t("pwreset.resent", {
          defaultValue: "New SMS sent ({{used}}/{{max}})",
          used: data.sends_used,
          max: 3,
        }),
      );
      setCooldown(60);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error_generic"));
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async (e) => {
    e?.preventDefault?.();
    setCodeErr("");
    if (!requestId) {
      // Anti-enumeration: the identifier didn't match, so no code was
      // actually sent. Show a benign error and let the user go back to
      // step 1 without confirming that the account doesn't exist.
      setCodeErr(
        t("pwreset.invalid_code_generic", {
          defaultValue: "The code you entered is not valid or has expired.",
        }),
      );
      return;
    }
    if (code.length !== 6) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/password-reset/verify", {
        request_id: requestId,
        code: code.trim(),
      });
      setVerificationToken(data.verification_token);
      setStep(3);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setCodeErr(
        detail || t("pwreset.invalid_code_generic", {
          defaultValue: "The code you entered is not valid or has expired.",
        }),
      );
    } finally {
      setBusy(false);
    }
  };

  const submitNewPassword = async (e) => {
    e?.preventDefault?.();
    if (!verificationToken || !allRulesOk || !passwordsMatch) return;
    setBusy(true);
    try {
      await api.post("/auth/password-reset/complete", {
        verification_token: verificationToken,
        new_password: pw1,
      });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error_generic"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell testId="forgot-password-page">
      <button
        onClick={() => navigate("/login")}
        className="inline-flex items-center gap-1 text-xs font-semibold text-white/85 hover:text-white mb-4"
        data-testid="forgot-back"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        {t("auth.back_to_login")}
      </button>

      <StepIndicator step={done ? 4 : step} />

      {done ? (
        <SuccessPanel onContinue={() => navigate("/login")} />
      ) : step === 1 ? (
        <IdentifyStep
          identifier={identifier}
          setIdentifier={(v) => {
            setIdentifier(v);
            setIdentifierTouched(false);
          }}
          onBlur={() => setIdentifierTouched(true)}
          idInfo={idInfo}
          showError={identifierTouched && !idInfo.ok && identifier.length > 0}
          busy={busy}
          onSubmit={startReset}
        />
      ) : step === 2 ? (
        <VerifyStep
          code={code}
          setCode={(v) => {
            setCode(v);
            if (codeErr) setCodeErr("");
          }}
          codeErr={codeErr}
          busy={busy}
          cooldown={cooldown}
          genericSent={genericSent}
          onResend={resend}
          onSubmit={verifyCode}
          onChangeIdentifier={() => {
            setStep(1);
            setCode("");
            setRequestId(null);
            setGenericSent(false);
            setCodeErr("");
          }}
        />
      ) : (
        <NewPasswordStep
          pw1={pw1}
          setPw1={setPw1}
          pw2={pw2}
          setPw2={setPw2}
          rules={ruleState}
          allRulesOk={allRulesOk}
          passwordsMatch={passwordsMatch}
          pw2Touched={pw2.length > 0}
          busy={busy}
          onSubmit={submitNewPassword}
        />
      )}
    </AuthShell>
  );
}

// ---------------------------------------------------------------------------
// Sub-components — keep each step focussed and small.
// ---------------------------------------------------------------------------

function IdentifyStep({
  identifier,
  setIdentifier,
  onBlur,
  idInfo,
  showError,
  busy,
  onSubmit,
}) {
  const { t } = useTranslation();
  return (
    <form onSubmit={onSubmit} className="space-y-5" data-testid="forgot-form-step1">
      <h1
        className="text-4xl sm:text-[2.5rem] font-extrabold tracking-tight leading-[1.05] text-white"
        style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
      >
        {t("pwreset.title", { defaultValue: "Forgot your password?" })}
      </h1>
      <p className="text-white/80 text-sm">
        {t("pwreset.step1_sub_v2", {
          defaultValue:
            "Enter your registered email or your registered mobile phone number. We'll send a 6-digit code via SMS to the phone registered on your account.",
        })}
      </p>
      <div>
        <label
          className="block text-[11px] font-bold uppercase tracking-widest text-white/85 mb-2"
          htmlFor="forgot-identifier-input"
        >
          {t("pwreset.identifier_label_v2", {
            defaultValue: "Email or mobile phone number",
          })}
        </label>
        <div className="relative">
          <AtSign className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 z-10" />
          <input
            id="forgot-identifier-input"
            data-testid="forgot-identifier-input"
            type="text"
            inputMode="email"
            autoComplete="username"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            onBlur={onBlur}
            required
            autoFocus
            aria-invalid={showError || undefined}
            className={
              "flex h-12 w-full rounded-xl border bg-white pl-10 pr-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 " +
              (showError
                ? "border-rose-300 focus:ring-rose-300"
                : "border-white/20 focus:ring-white/60")
            }
            placeholder={t("pwreset.identifier_placeholder_v2", {
              defaultValue:
                "Enter your registered email or your registered mobile phone number",
            })}
          />
        </div>
        <div className="min-h-[1.25rem] mt-1.5">
          {showError ? (
            <span
              className="text-xs text-rose-200"
              data-testid="forgot-identifier-error"
            >
              {t("pwreset.identifier_invalid", {
                defaultValue:
                  "Enter a valid email (name@example.com) or a phone in E.164 format (e.g. +56961234567).",
              })}
            </span>
          ) : (
            <span
              className="text-xs text-white/60"
              data-testid="forgot-identifier-hint"
            >
              {t("pwreset.identifier_examples", {
                defaultValue: "Examples: john@example.com  ·  +56961234567",
              })}
            </span>
          )}
        </div>
      </div>
      <button
        type="submit"
        disabled={busy || !idInfo.ok}
        data-testid="forgot-submit"
        className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#4098FF] to-[#0F62FE] text-white font-bold hover:from-[#5AA9FF] hover:to-[#1E70FE] disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-lg"
      >
        {busy && <Loader2 className="w-4 h-4 animate-spin" />}
        {busy
          ? t("auth.sending", { defaultValue: "Sending…" })
          : t("pwreset.send_sms", { defaultValue: "Send verification code" })}
      </button>
    </form>
  );
}

function VerifyStep({
  code,
  setCode,
  codeErr,
  busy,
  cooldown,
  onResend,
  onSubmit,
  onChangeIdentifier,
}) {
  const { t } = useTranslation();
  return (
    <form onSubmit={onSubmit} className="space-y-5" data-testid="forgot-form-step2">
      <h1
        className="text-4xl sm:text-[2.25rem] font-extrabold tracking-tight leading-[1.05] text-white"
        style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
      >
        {t("pwreset.step2_title", { defaultValue: "Enter the SMS code" })}
      </h1>
      <div
        className="rounded-xl border border-white/20 bg-white/10 text-white/90 text-sm px-4 py-3 backdrop-blur"
        data-testid="forgot-generic-sent"
      >
        {t("pwreset.step2_generic_v2", {
          defaultValue:
            "If the information you entered is valid, a verification code has been sent. It expires in 5 minutes.",
        })}
      </div>
      <div>
        <label
          className="block text-[11px] font-bold uppercase tracking-widest text-white/85 mb-2"
          htmlFor="forgot-code-input"
        >
          {t("pwreset.code_label", { defaultValue: "6-digit code" })}
        </label>
        <div className="relative">
          <MessageSquare className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 z-10" />
          <input
            id="forgot-code-input"
            data-testid="forgot-code-input"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            required
            autoFocus
            aria-invalid={codeErr ? true : undefined}
            className={
              "flex h-14 w-full rounded-xl border bg-white pl-10 pr-3 text-2xl font-mono tracking-[8px] font-bold text-slate-900 placeholder:text-slate-300 focus:outline-none focus:ring-2 " +
              (codeErr
                ? "border-rose-300 focus:ring-rose-300"
                : "border-white/20 focus:ring-white/60")
            }
            placeholder="••••••"
          />
        </div>
        {codeErr && (
          <div
            className="text-xs text-rose-200 mt-1.5"
            data-testid="forgot-code-error"
          >
            {codeErr}
          </div>
        )}
      </div>
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onResend}
          disabled={busy || cooldown > 0}
          data-testid="forgot-resend"
          className="text-xs font-bold text-white hover:underline disabled:text-white/40 disabled:cursor-not-allowed disabled:no-underline"
        >
          {cooldown > 0
            ? t("pwreset.resend_in", { defaultValue: "Resend in {{s}}s", s: cooldown })
            : t("pwreset.resend_sms", { defaultValue: "Resend SMS" })}
        </button>
        <button
          type="button"
          onClick={onChangeIdentifier}
          className="text-xs font-semibold text-white/75 hover:text-white"
          data-testid="forgot-change-identifier"
        >
          {t("pwreset.change_identifier_v2", {
            defaultValue: "Use a different email or phone",
          })}
        </button>
      </div>
      <button
        type="submit"
        disabled={busy || code.length !== 6}
        data-testid="forgot-verify"
        className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#4098FF] to-[#0F62FE] text-white font-bold hover:from-[#5AA9FF] hover:to-[#1E70FE] disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-lg"
      >
        {busy && <Loader2 className="w-4 h-4 animate-spin" />}
        {busy
          ? t("common.verifying", { defaultValue: "Verifying…" })
          : t("pwreset.verify", { defaultValue: "Verify code" })}
      </button>
    </form>
  );
}

function NewPasswordStep({
  pw1,
  setPw1,
  pw2,
  setPw2,
  rules,
  allRulesOk,
  passwordsMatch,
  pw2Touched,
  busy,
  onSubmit,
}) {
  const { t } = useTranslation();
  const submitDisabled = busy || !allRulesOk || !passwordsMatch;
  return (
    <form onSubmit={onSubmit} className="space-y-5" data-testid="forgot-form-step3">
      <h1
        className="text-4xl sm:text-[2.25rem] font-extrabold tracking-tight leading-[1.05] text-white"
        style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
      >
        {t("pwreset.step3_title", { defaultValue: "Create a new password" })}
      </h1>
      <p className="text-white/80 text-sm">
        {t("pwreset.step3_sub_v2", {
          defaultValue:
            "Choose a strong password. All requirements below must be met.",
        })}
      </p>
      <div>
        <label
          className="block text-[11px] font-bold uppercase tracking-widest text-white/85 mb-2"
          htmlFor="forgot-new-password"
        >
          {t("pwreset.new_password", { defaultValue: "New password" })}
        </label>
        <div className="relative">
          <KeyRound className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 z-10" />
          <PasswordInput
            id="forgot-new-password"
            data-testid="forgot-new-password"
            value={pw1}
            onChange={(e) => setPw1(e.target.value)}
            required
            minLength={8}
            autoFocus
            autoComplete="new-password"
            className="flex h-12 w-full rounded-xl border border-white/20 bg-white pl-10 pr-10 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-white/60"
          />
        </div>
      </div>

      <ul
        className="grid gap-1.5 rounded-xl border border-white/20 bg-white/10 backdrop-blur px-3 py-2.5"
        data-testid="forgot-rules"
      >
        {rules.map((r) => (
          <li
            key={r.key}
            data-testid={`forgot-rule-${r.key}`}
            data-ok={r.ok ? "true" : "false"}
            className={
              "flex items-center gap-2 text-xs " +
              (r.ok ? "text-emerald-200" : "text-white/70")
            }
          >
            {r.ok ? (
              <CheckCircle2 className="w-3.5 h-3.5" />
            ) : (
              <XCircle className="w-3.5 h-3.5 text-white/30" />
            )}
            {r.label}
          </li>
        ))}
      </ul>

      <div>
        <label
          className="block text-[11px] font-bold uppercase tracking-widest text-white/85 mb-2"
          htmlFor="forgot-confirm-password"
        >
          {t("pwreset.confirm_password", { defaultValue: "Confirm new password" })}
        </label>
        <div className="relative">
          <KeyRound className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 z-10" />
          <PasswordInput
            id="forgot-confirm-password"
            data-testid="forgot-confirm-password"
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
            aria-invalid={pw2Touched && !passwordsMatch ? true : undefined}
            className={
              "flex h-12 w-full rounded-xl border bg-white pl-10 pr-10 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 " +
              (pw2Touched && !passwordsMatch
                ? "border-rose-300 focus:ring-rose-300"
                : "border-white/20 focus:ring-white/60")
            }
          />
        </div>
        {pw2Touched && !passwordsMatch && (
          <div
            className="text-xs text-rose-200 mt-1.5"
            data-testid="forgot-mismatch"
          >
            {t("pwreset.mismatch", { defaultValue: "Passwords do not match." })}
          </div>
        )}
      </div>

      <button
        type="submit"
        disabled={submitDisabled}
        data-testid="forgot-complete"
        className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#4098FF] to-[#0F62FE] text-white font-bold hover:from-[#5AA9FF] hover:to-[#1E70FE] disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-lg"
      >
        {busy && <Loader2 className="w-4 h-4 animate-spin" />}
        {busy
          ? t("pwreset.updating", { defaultValue: "Updating…" })
          : t("pwreset.reset_password", { defaultValue: "Reset password" })}
      </button>
    </form>
  );
}

function SuccessPanel({ onContinue }) {
  const { t } = useTranslation();
  return (
    <div className="text-center py-6" data-testid="forgot-done">
      <CheckCircle2 className="w-16 h-16 text-emerald-300 mx-auto" />
      <h2
        className="text-2xl font-extrabold text-white mt-4"
        style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
      >
        {t("pwreset.done_title_v2", { defaultValue: "Password updated successfully" })}
      </h2>
      <p className="text-white/80 mt-2 text-sm">
        {t("pwreset.done_sub_v2", {
          defaultValue: "Your password has been updated successfully.",
        })}
      </p>
      <button
        onClick={onContinue}
        className="mt-6 inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#4098FF] to-[#0F62FE] px-6 text-white font-bold hover:from-[#5AA9FF] hover:to-[#1E70FE] shadow-lg"
        data-testid="forgot-goto-login"
      >
        {t("pwreset.back_to_login", { defaultValue: "Back to sign in" })}
      </button>
    </div>
  );
}

function StepIndicator({ step }) {
  const { t } = useTranslation();
  const items = [
    { n: 1, label: t("pwreset.step_identify", { defaultValue: "Identify" }) },
    { n: 2, label: t("pwreset.step_verify", { defaultValue: "Verify" }) },
    { n: 3, label: t("pwreset.step_new", { defaultValue: "New password" }) },
  ];
  return (
    <div className="flex items-center gap-2 mb-5" data-testid="forgot-step-indicator">
      {items.map((it, idx) => {
        const active = step === it.n;
        const isDone = step > it.n;
        return (
          <React.Fragment key={it.n}>
            <div
              className={
                "flex items-center gap-1.5 " +
                (active
                  ? "text-white"
                  : isDone
                    ? "text-emerald-200"
                    : "text-white/50")
              }
              data-testid={`forgot-step-${it.n}${active ? "-active" : isDone ? "-done" : ""}`}
            >
              <div
                className={
                  "w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold " +
                  (active
                    ? "bg-white text-[#0F62FE]"
                    : isDone
                      ? "bg-emerald-400 text-white"
                      : "bg-white/20 text-white/80")
                }
              >
                {isDone ? "✓" : it.n}
              </div>
              <span className="text-xs font-semibold uppercase tracking-widest hidden sm:inline">
                {it.label}
              </span>
            </div>
            {idx < items.length - 1 && (
              <div
                className={
                  "h-0.5 flex-1 " + (step > it.n ? "bg-emerald-300" : "bg-white/20")
                }
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
